import React, { useContext, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Card,
  Col,
  DatePicker,
  FloatButton,
  Form,
  Input,
  InputNumber,
  Modal,
  notification,
  Popconfirm,
  Row,
  Select,
  Space,
  Switch,
  Table,
  TimePicker,
  Tooltip,
} from 'antd';
import {
  DeleteFilled,
  EditFilled,
  HolderOutlined,
  PlusOutlined,
  SwapOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import AjaxTable from '../../../components/AjaxTable';
import {
  createQuestion,
  deleteQuestion,
  queryQuestions,
  questionSetOrder,
  updateQuestion,
} from '../../../api/module/question';
import { CustomButton, CustomSelector } from '../../../components';
import { getQuestionTypes } from '../../../api/module/question_type';
import {
  createQuestionOption,
  deleteQuestionOption,
  queryQuestionOptions,
  updateQuestionOption,
} from '../../../api/module/question_option';
import Title from 'antd/es/typography/Title';
import { catchException } from '../../../utils/helpers/helpers';
import { checkUsageQuestion } from '../../../api/module/fill_out_form_question';
import {
  createQuestionValidation,
  deleteQuestionValidation,
  queryQuestionValidation,
  updateQuestionValidation,
} from '../../../api/module/question_validation';
import { queryQuestionValidationCatalog } from '../../../api/module/question_validation_catalog';
import dayjs from 'dayjs';
import { querySections } from '../../../api/module/section_form';
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { DndContext } from '@dnd-kit/core';
import { restrictToVerticalAxis } from '@dnd-kit/modifiers';
import { CSS } from '@dnd-kit/utilities';
import SpinLoad from '../../../components/SpinLoad';

const FormQuestion = ({dataQuestion, saveQuestion, questionType, enableFields, createQuestionFlag, saving, resetFormQuestion}) => {
  const [formQuestion] = Form.useForm();
  return (
    <>
      <Form
        form={formQuestion}
        layout="vertical"
        name="handleQuestion"
        initialValues={dataQuestion}
        onFinish={saveQuestion}
        preserve={false}
      >
        <Row gutter={40}>
          <Col span={24}>
            <Form.Item
              name="order"
              hidden={true}
            >
              <Input />
            </Form.Item>
            <Form.Item
              label="Tipo:"
              name="tipo"
              rules={[
                { required: true, message: 'Debe seleccionar un tipo de pregunta.' },
              ]}
            >
              <CustomSelector
                selectorProps={{
                  style: { width: '100%' },
                  placeholder:
                    'Seleccione el tipo de pregunta... ',
                }}
                endpointFetch={async () => {
                  return await getQuestionTypes();
                }}
                initialValue={questionType}
                onChange={(value) => enableFields(value)}
              />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={40}>
          <Col span={24}>
            <Form.Item
              label="Nombre de pregunta:"
              name="nombre"
              rules={[
                { required: true, message: 'Por favor ingrese el nombre de la pregunta.' },
                { max: 255, message: 'El nombre no puede tener más de 255 caracteres' }
              ]}
            >
              <Input />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={40}>
          <Col span={24}>
            <Form.Item
              label="Etiqueta:"
              name="etiqueta"
              rules={[
                { required: true, message: 'Por favor ingrese la etiqueta de la pregunta.' },
                { max: 255, message: 'El nombre no puede tener más de 70 caracteres' }
              ]}
            >
              <Input />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={40}>
          <Col span={24}>
            <Form.Item
              label="Mensaje Informativo:"
              name="tooltip"
            >
              <Input />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={40}>
          <Col span={6}>
            <Form.Item
              name='obligatorio'
              label='Obligatorio'
              valuePropName='checked'
            >
              <Switch />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item
              name='editable'
              label='Editable'
              valuePropName='checked'
            >
              <Switch />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item
              name='grid'
              label='Grid'
              valuePropName='checked'
            >
              <Switch />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item
              name='activa'
              label='Activa'
              valuePropName='checked'
            >
              <Switch />
            </Form.Item>
          </Col>
        </Row>
        <Row justify={'center'}>
          <Col span={5}>
            <Form.Item>
              <CustomButton
                type="primary"
                htmlType="submit"
                text={createQuestionFlag ? 'Guardar' : 'Actualizar'}
                loading={saving}
              />
            </Form.Item>
          </Col>
          <Col span={5} style={{ marginLeft: '25px' }}>
            <Form.Item>
              <CustomButton
                type="default"
                htmlType="button"
                text="Cerrar"
                onClick={resetFormQuestion}
              />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </>
  );
}

const FormQuestionOption = ({question}) => {
  const [formQuestionOption] = Form.useForm();
  const [createOptionFlag, setCreateOptionFlag] = useState(true);
  const [savingOptionLoader, setSavingOptionLoader] = useState(false);
  const [reloadOptionList, setReloadOptionList] = useState(false);
  const [optionFilter] = useState({'filter[pregunta]': question});
  const [optionId, setOptionId] = useState(undefined);

  const columnsOptionList = [
    {
      title: 'Cod',
      dataIndex: 'id',
      key: 'id',
    },
    {
      title: 'Opción',
      dataIndex: 'opcion',
      key: 'opcion',
    },
    {
      title: 'Estado',
      dataIndex: 'activa',
      key: 'activa',
      render: (text) => (text ? 'A' : 'I'),
    },
    {
      title: 'Acciones',
      dataIndex: 'accion',
      align: 'center',
      fixed: 'right',
      render: (_, record) => (
        <Space>
          <Tooltip title='Editar Opción' placement='top'>
            <EditFilled
              onClick={() => editOption(record)}
              style={{ fontSize: '16px', color: 'green' }}
            />
          </Tooltip>
          <Tooltip title='Borrar Opción' placement='top'>
            <Popconfirm
              title="Borrar Opción"
              description='Está seguro de borrar la opción de la pregunta ?'
              onConfirm={() => deleteOption(record.id)}
              icon={<WarningOutlined style={{ color: 'red' }} />}
            >
              <DeleteFilled style={{ fontSize: '16px', color: 'red' }} />
            </Popconfirm>
          </Tooltip>
        </Space>
      ),
    },
  ];

  const resetFormQuestionOption = () => {
    formQuestionOption.resetFields();
    setCreateOptionFlag(true);
    setOptionId(undefined);
  };

  const saveOption = async (values) => {
    setSavingOptionLoader(true);
    setReloadOptionList(false);
    try {
      values.pregunta = question;
      if (createOptionFlag) {
        let optionCreated = await createQuestionOption(values);
        notification.success({
          message: 'Creación de Opción Pregunta',
          description: `La opción de pregunta ha sido creado con ID: ${optionCreated.data.id}`,
          duration: 3,
        });
      } else {
        let optionUpdated = await updateQuestionOption(optionId, values);
        notification.success({
          message: 'Actualización de Opción Pregunta',
          description: `La opcion de pregunta con ID ${optionUpdated.data.id} ha sido actualizada.`,
          duration: 3,
        });
      }
    } catch (e) {
      catchException(e);
    } finally {
      setSavingOptionLoader(false);
      setReloadOptionList(true);
      resetFormQuestionOption();
    }
  };

  const editOption = (record) => {
    setOptionId(record.id);
    formQuestionOption.setFieldsValue(record);
    setCreateOptionFlag(false);
  };

  const deleteOption = async (id) => {
    try {
      // TODO - Check dependencies before to delete
      await deleteQuestionOption(id);
    } catch (e) {
      catchException(e);
    } finally {
      setReloadOptionList(true);
    }
  }

  return (
    <>
      <Card
        variant='outlined'
        title={<Title level={4}>Opciones de Pregunta</Title>}
      >
        <Form
          form={formQuestionOption}
          layout="vertical"
          name="handleQuestionOption"
          onFinish={saveOption}
          preserve={false}
        >
          <Row gutter={40}>
            <Col span={24}>
              <Form.Item
                label="Opción:"
                name="opcion"
                rules={[
                  { required: true, message: 'Por favor ingrese la opción de la pregunta.' },
                  { max: 70, message: 'El nombre no puede tener más de 70 caracteres' }
                ]}
              >
                <Input />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={40}>
            <Col span={24}>
              <Form.Item
                name='activa'
                label='Activa'
                valuePropName='checked'
              >
                <Switch />
              </Form.Item>
            </Col>
          </Row>
          <Row justify={'center'}>
            <Col span={5}>
              <Form.Item>
                <CustomButton
                  type="primary"
                  htmlType="submit"
                  text={createOptionFlag ? 'Guardar' : 'Actualizar'}
                  loading={savingOptionLoader}
                />
              </Form.Item>
            </Col>
            <Col span={5} style={{ marginLeft: '30px' }}>
              <Form.Item>
                <CustomButton
                  type="default"
                  htmlType="button"
                  text="Reset"
                  onClick={resetFormQuestionOption}
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Card>
      <AjaxTable
        tableTitle={'Lista de Opciones de pregunta'}
        rowKey={'id'}
        endpoint={queryQuestionOptions}
        query={optionFilter}
        columns={columnsOptionList}
        searchInput={true}
        reloadSource={reloadOptionList}
      />
    </>
  );
}

const FormQuestionValidation = ({question, questionType}) => {
  const [formQuestionValidation] = Form.useForm();
  const [reloadValidationList, setReloadValidationList] = useState(false);
  const [createValidationFlag, setCreateValidationFlag] = useState(true);
  const [savingValidationLoader, setSavingValidationLoader] = useState(false);
  const [validationFilter] = useState({'filter[pregunta]': question});
  const [showValueFinal, setShowValueFinal] = useState(false);
  const [validationId, setValidationId] = useState(undefined);
  const [optionsValidationType, setOptionsValidationType] = useState([]);

  const columnsOptionList = [
    {
      title: 'Id',
      dataIndex: 'id',
      key: 'id',
    },
    {
      title: 'Validación',
      dataIndex: 'validacion_nombre',
      key: 'validacion_nombre',
    },
    {
      title: 'Valor',
      dataIndex: 'valor_inicial',
      key: 'valor_inicial',
    },
    {
      title: 'Valor Final',
      dataIndex: 'valor_final',
      key: 'valor_final',
    },
    {
      title: 'Acciones',
      dataIndex: 'accion',
      align: 'center',
      fixed: 'right',
      render: (_, record) => (
        <Space>
          <Tooltip title='Editar Validación' placement='top'>
            <EditFilled
              onClick={() => editValidation(record)}
              style={{ fontSize: '16px', color: 'green' }}
            />
          </Tooltip>
          <Tooltip title="Eliminar Validación" placement="top">
            <Popconfirm
              title="Eliminar Validación"
              description="Está seguro de eliminar esta validación ?"
              onConfirm={() => deleteValidation(record.id)}
              icon={<WarningOutlined style={{ color: 'red' }} />}>
              <DeleteFilled style={{ fontSize: '16px', color: 'red' }} />
            </Popconfirm>
          </Tooltip>
        </Space>
      ),
    },
  ];

  const saveValidation = async (values) => {
    setSavingValidationLoader(true);
    setReloadValidationList(false);
    try {
      values.pregunta = question;
      let data = transformData(values);
      if (createValidationFlag) {
        let validationCreated = await createQuestionValidation(data);
        notification.success({
          message: 'Creación de Validación de Pregunta',
          description: `La validación de pregunta ha sido creada con ID: ${validationCreated.data.id}`,
          duration: 3,
        });
      } else {
        let validationUpdated = await updateQuestionValidation(validationId, data);
        notification.success({
          message: 'Actualización de Validación de Pregunta',
          description: `La validación de pregunta con ID ${validationUpdated.data.id} ha sido actualizada.`,
          duration: 3,
        });
      }
    } catch (e) {
      catchException(e);
    } finally {
      setSavingValidationLoader(false);
      setReloadValidationList(true);
      resetFormQuestionValidation();
    }
  };

  const editValidation = async (record) => {
    setValidationId(record.id);
    let data = convertData(record);
    formQuestionValidation.setFieldsValue(data);
    if ([9,12].includes(record.validacion)) {
      setShowValueFinal(true);
    }
    setCreateValidationFlag(false);
  };

  const deleteValidation = async (id) => {
    try {
      await deleteQuestionValidation(id);
    } catch (e) {
      catchException(e);
    } finally {
      setReloadValidationList(true);
    }
  };

  const resetFormQuestionValidation = () => {
    formQuestionValidation.resetFields();
    setCreateValidationFlag(true);
    setValidationId(undefined);
    setShowValueFinal(false);
  };

  const getCategoryType = () => {
    let category;
    switch (questionType) {
      case 3:
      case 10:
        category = "text";
        break;
      default:
        category = "number";
    }
    return category;
  };

  const getValidationTypes = async () => {
    try {
      return await queryQuestionValidationCatalog({"filter[tipo]": getCategoryType()})
    } catch (e) {
      catchException(e);
    }
  };

  const transformData = (values) => {
    let data = {};
    Object.entries(values).forEach(([key, value]) => {
      if (["valor_inicial", "valor_final"].includes(key)) {
        switch (questionType) {
          case 4:
            data[key] = value.format("YYYY-MM-DD");
            break;
          case 5:
            data[key] = value.format("HH:mm:ss");
            break;
          default:
            data[key] = value;
        }
      } else data[key] = value;
    });
    return data;
  };

  const convertData = (values) => {
    let data = {};
    Object.entries(values).forEach(([key, value]) => {
      if (["valor_inicial", "valor_final"].includes(key)) {
        switch (questionType) {
          case 4:
            data[key] = value ? dayjs(value, "YYYY-MM-DD") : null;
            break;
          case 5:
            data[key] = value ? dayjs(value, "HH:mm:ss") : null;
            break;
          case 11:
            data[key] = value ? dayjs(value) : null;
            break;
          default:
            data[key] = value;
        }
      } else data[key] = value;
    });
    return data;
  };

  useEffect(() => {
    getValidationTypes().then((data) => {
      setOptionsValidationType(data.data.results.map(option => ({ label: option.nombre, value: option.id })));
    })
  }, []);

  return (
    <>
      <Card
        variant='outlined'
        title={<Title level={4}>Validaciones de Pregunta</Title>}
      >
        <Form
          form={formQuestionValidation}
          layout="vertical"
          name="handleQuestionValidation"
          onFinish={saveValidation}
          preserve={false}
        >
          <Row gutter={40}>
            <Col span={8}>
              <Form.Item
                label="Validación:"
                name="validacion"
                rules={[{required: true, message: 'Es requerido el tipo de validación.'}]}
              >
                <Select
                  options={optionsValidationType}
                  onChange={(value) => { ([9,12].includes(value) ? setShowValueFinal(true) : setShowValueFinal(false)) }}
                  placeholder="Seleccione un tipo de validación..."
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="Valor:"
                name="valor_inicial"
                rules={[{ required: true, message: 'Es requerido el valor de la validación.' },]}
              >
                {[3, 10].includes(questionType) && (
                  <Input />
                )}
                {[6,13].includes(questionType) && (
                  <InputNumber style={{ width: '100%' }} />
                )}
                {[4].includes(questionType) && (
                  <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
                )}
                {[5].includes(questionType) && (
                  <TimePicker style={{ width: '100%' }} format="HH:mm:ss" />
                )}
                {[11].includes(questionType) && (
                  <DatePicker showTime style={{ width: '100%' }} format="YYYY-MM-DD" />
                )}
              </Form.Item>
            </Col>
            <Col span={8}>
              {showValueFinal && (
                <Form.Item
                  label="Valor Final:"
                  name="valor_final"
                >
                  {[3, 10].includes(questionType) && (
                    <Input />
                  )}
                  {[6,13].includes(questionType) && (
                    <InputNumber style={{ width: '100%' }} />
                  )}
                  {[4].includes(questionType) && (
                    <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
                  )}
                  {[5].includes(questionType) && (
                    <TimePicker style={{ width: '100%' }} format="HH:mm:ss" />
                  )}
                  {[11].includes(questionType) && (
                    <DatePicker showTime style={{ width: '100%' }} format="YYYY-MM-DD" />
                  )}
                </Form.Item>
              )}
            </Col>
          </Row>
          <Row gutter={40}>
            <Col span={24}>
              <Form.Item
                label="Mensaje de error:"
                name="error_message"
                rules={[
                  { required: false, message: 'Por favor ingrese el mensaje de error de la validación.' },
                  { max: 70, message: 'El mensaje no puede tener más de 70 caracteres' }
                ]}
              >
                <Input />
              </Form.Item>
            </Col>
          </Row>
          <Row justify={'center'}>
            <Col span={5}>
              <Form.Item>
                <CustomButton
                  type="primary"
                  htmlType="submit"
                  text={createValidationFlag ? 'Guardar' : 'Actualizar'}
                  loading={savingValidationLoader}
                />
              </Form.Item>
            </Col>
            <Col span={5} style={{ marginLeft: '30px' }}>
              <Form.Item>
                <CustomButton
                  type="default"
                  htmlType="button"
                  text="Reset"
                  onClick={resetFormQuestionValidation}
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Card>
      <AjaxTable
        tableTitle={'Lista de Validaciones de pregunta'}
        rowKey={'id'}
        endpoint={queryQuestionValidation}
        query={validationFilter}
        columns={columnsOptionList}
        searchInput={true}
        reloadSource={reloadValidationList}
      />
    </>)
};

export default function Section({sectionId, formularioId, changeTab}) {
  const [questionFilter] = useState({ 'filter[seccion]': sectionId });
  const [loaderQuestions, setLoaderQuestions] = useState(false);
  const [savingQuestion, setSavingQuestion] = useState(false);
  const [openModalQuestion, setOpenModalQuestion] = useState(false);
  const [openModalChangeQuestionSection, setOpenModalChangeQuestionSection] = useState(false);
  const [changeQuestionSection, setChangeQuestionSection] = useState(false);
  const [createQuestionFlag, setCreateQuestionFlag] = useState(true);
  const [loadForm, setLoadForm] = useState(false);
  const [dataQuestion, setDataQuestion] = useState({'obligatorio': true, 'activa': true});
  const [questionType, setQuestionType] = useState(undefined);
  const [questionId, setQuestionId] = useState(undefined);
  const [sectionListOptions, setSectionListOptions] = useState([]);
  const [sectionList, setSectionList] = useState([]);
  const [orderChangeSection, setOrderChangeSection] = useState(undefined);
  const [formChangeQuestionSection] = Form.useForm();
  const [dataSource, setDataSource] = React.useState([]);
  const RowContext = React.createContext({});

  const columnsQuestionList = [
    { key: 'sort', align: 'center', width: 80, render: () => <DragHandle /> },
    {
      title: 'Cod',
      dataIndex: 'id',
      key: 'id',
    },
    {
      title: 'Pregunta',
      dataIndex: 'nombre',
      width: '40%',
      key: 'nombre',
    },
    {
      title: 'Tipo',
      dataIndex: 'tipo_nombre',
      key: 'tipo_nombre',
    },
    {
      title: 'Etiqueta',
      dataIndex: 'etiqueta',
      key: 'etiqueta',
    },
    {
      title: 'Obligatorio',
      dataIndex: 'obligatorio',
      key: 'obligatorio',
      render: (text) => (text ? 'Sí' : 'No'),
    },
    {
      title: 'Estado',
      dataIndex: 'activa',
      key: 'activa',
      render: (text) => (text ? 'A' : 'I'),
    },
    {
      title: 'Acciones',
      dataIndex: 'accion',
      align: 'center',
      fixed: 'right',
      render: (_, record) => (
        <Space>
          <Tooltip title='Editar Pregunta' placement='top'>
            <EditFilled
              onClick={() => editQuestion(record)}
              style={{ fontSize: '16px', color: 'green' }}
            />
          </Tooltip>
          {/* TODO - Crear lógica para clonar preguntas */}
          {/*<Tooltip title='Clonar Pregunta' placement='top'>*/}
          {/*  <CopyFilled onClick={() => {}} />*/}
          {/*</Tooltip>*/}
          <Tooltip title='Borrar Pregunta' placement='top'>
            <Popconfirm
              title='Eliminar Pregunta'
              description='Está seguro de eliminar esta pregunta ?'
              onConfirm={() => removeQuestion(record.id)}
              icon={<WarningOutlined style={{ color: 'red' }} />}>
              <DeleteFilled style={{ fontSize: '16px', color: 'red' }}/>
            </Popconfirm>
          </Tooltip>
          <Tooltip title='Mover Pregunta a otra Sección' placement='top'>
            <SwapOutlined
              onClick={() => changeSection(record)}
              style={{ fontSize: '16px', color: 'orange' }}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  const editQuestion = (record) => {
    setDataQuestion(record);
    setCreateQuestionFlag(false);
    setQuestionType(record.tipo);
    setQuestionId(record.id);
    setOpenModalQuestion(true);
  };

  const resetFormQuestion = () => {
    setDataQuestion({'obligatorio': true, 'activa': true});
    setQuestionType(undefined);
    setCreateQuestionFlag(true);
    setOpenModalQuestion(false);
  };

  const saveQuestion = async (values) => {
    setSavingQuestion(true);
    try {
      if (changeQuestionSection === false) {
        values.seccion = sectionId;
      } else {
        values.order = orderChangeSection;
      }
      if (createQuestionFlag) {
        values.order = dataSource.length
        let recordCreated = await createQuestion(values);
        notification.success({
          message: 'Creación de Pregunta',
          description: `La pregunta ha sido creado con ID: ${recordCreated.data.id}`,
          duration: 3,
        });
      } else {
        let recordUpdated = await updateQuestion(dataQuestion.id, values);
        notification.success({
          message: 'Actualización de Pregunta',
          description: `La pregunta con ID ${recordUpdated.data.id} ha sido actualizada.`,
          duration: 3,
        });
        if (changeQuestionSection) {
          changeTab(`seccion-${values.seccion}`);
        }
      }
    } catch (error) {
      catchException(error);
    } finally {
      setSavingQuestion(false);
      resetFormQuestion();
      reloadQuestionList();
      resetFormChangeQuestionSection();
    }
  };

  const removeQuestion = async (id) => {
    try {
      let usage = await checkUsageQuestion(id)
      if (usage.data.results.length === 0) {
        await deleteQuestion(id);
        notification.success({
          message: 'Eliminación de Pregunta',
          description: `La pregunta con ID ${id} ha sido eliminada exitosamente.`,
          duration: 3,
        });
      } else {
        notification.error({
          message: 'Eliminación de Pregunta',
          description: `La pregunta con ID ${id} no se puede eliminar, debido a que tiene diligenciamientos.`,
          duration: 4,
        });
      }
    } catch (e) {
      catchException(e);
    } finally {
      reloadQuestionList();
    }
  };

  const enableFields = (value) => {
    setQuestionType(value);
  };

  const resetFormChangeQuestionSection = () => {
    setDataQuestion({'obligatorio': true, 'activa': true});
    setCreateQuestionFlag(true);
    setOpenModalChangeQuestionSection(false);
    setChangeQuestionSection(false);
  };

  const changeSection = (record) => {
    setLoaderQuestions(true);
    fetchSections().then((_) => {
      setDataQuestion(record);
      setOpenModalChangeQuestionSection(true);
      setCreateQuestionFlag(false);
      setChangeQuestionSection(true);
      setLoadForm(false);
      setLoaderQuestions(false);
    });
  }

  const fetchSections = async () => {
    try {
      let sections = await querySections({"filter[formulario]": formularioId}, 1, 40, '', false)
      setSectionListOptions(sections.data.results.filter(o => o.id !== sectionId).map(section => ({label: section.nombre, value: section.id})));
      setSectionList(sections.data.results);
      return sections.data.results;
    } catch(error) {
      catchException(error);
    }
  };

  const fetchQuestions = async () => {
    setLoaderQuestions(true);
    try {
      let questions = await queryQuestions(questionFilter, 1, 100, '', false)
      return questions.data.results;
    } catch (e) {
      catchException(e);
    } finally {
      setLoaderQuestions(false);
    }
  };

  const reloadQuestionList = () => {
    fetchQuestions().then(r => setDataSource(r));
  }

  useEffect(() => {
    reloadQuestionList();
  }, [questionFilter]);

  const DragHandle = () => {
    const { setActivatorNodeRef, listeners } = useContext(RowContext);
    return (
      <Button
        type="text"
        size="small"
        icon={<HolderOutlined />}
        style={{ cursor: 'move' }}
        ref={setActivatorNodeRef}
        {...listeners}
      />
    );
  };

  const RowData = props => {
    const {
      attributes,
      listeners,
      setNodeRef,
      setActivatorNodeRef,
      transform,
      transition,
      isDragging,
    } = useSortable({ id: props['data-row-key'] });
    const style = {
      ...props.style,
      transform: CSS.Translate.toString(transform),
      transition,
      ...(isDragging ? { position: 'relative', zIndex: 9999 } : {}),
    };
    const contextValue = useMemo(
      () => ({ setActivatorNodeRef, listeners }),
      [setActivatorNodeRef, listeners],
    );
    return (
      <RowContext.Provider value={contextValue}>
        <tr {...props} ref={setNodeRef} style={style} {...attributes} />
      </RowContext.Provider>
    );
  };

  const onDragEnd = async ({ active, over }) => {
    if (active.id !== over?.id) {
      let newIndex;
      setDataSource(prevState => {
        const activeIndex = prevState.findIndex(record => record.id === active?.id);
        const overIndex = prevState.findIndex(record => record.id === over?.id);
        newIndex = overIndex;
        return arrayMove(prevState, activeIndex, overIndex);
      });
      setLoaderQuestions(true);
      try {
        await questionSetOrder(active.id, { 'newOrder': newIndex });
      } catch (e) {
        catchException(e);
      } finally {
        setLoaderQuestions(false);
      }
    }
  }

  return (
    <>
      <FloatButton
        shape='circle'
        type='primary'
        tooltip='Crear Pregunta'
        icon={<PlusOutlined />}
        style={{ insetInlineEnd: 50, width: '50px', height: '50px' }}
        onClick={() => setOpenModalQuestion(true)}
      />
      <SpinLoad loading={loaderQuestions}>
        <DndContext modifiers={[restrictToVerticalAxis]} onDragEnd={onDragEnd}>
          <SortableContext items={dataSource.map(i => i.id)} strategy={verticalListSortingStrategy}>
            <Table
              rowKey="id"
              components={{ body: { row: RowData } }}
              columns={columnsQuestionList}
              dataSource={dataSource}
              scroll={{ x: 'max-content' }}
              pagination={false}
            />
          </SortableContext>
        </DndContext>
      </SpinLoad>
      <Modal
        title={createQuestionFlag ? "Creación de pregunta" : "Editar Pregunta"}
        open={openModalQuestion}
        onCancel={resetFormQuestion}
        width={'70%'}
        footer={null}
        destroyOnClose
      >
        <FormQuestion
          dataQuestion={dataQuestion}
          saveQuestion={saveQuestion}
          questionType={questionType}
          enableFields={enableFields}
          createQuestionFlag={createQuestionFlag}
          saving={savingQuestion}
          resetFormQuestion={resetFormQuestion}
        />
        {!createQuestionFlag && [1,2].includes(questionType) && (
          <FormQuestionOption
            question={questionId}
          />
        )}
        {!createQuestionFlag && [3,4,5,6,10,11,13].includes(questionType) && (
          <FormQuestionValidation
            question={questionId}
            questionType={questionType}
          />
        )}
      </Modal>
      <Modal
        title='Mover Pregunta a Sección'
        open={openModalChangeQuestionSection}
        onCancel={resetFormChangeQuestionSection}
        footer={null}
        destroyOnClose
      >
        <Form
          form={formChangeQuestionSection}
          layout="vertical"
          name="changeQuestionSection"
          initialValues={dataQuestion}
          onFinish={saveQuestion}
          preserve={false}
        >
          <Row gutter={40}>
            <Col span={24}>
              <Form.Item
                hidden={true}
                name="tipo"
              >
                <Input />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={40}>
            <Col span={24}>
              <Form.Item
                hidden={true}
                name="nombre"
              >
                <Input />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={40}>
            <Col span={24}>
              <Form.Item
                hidden={true}
                name='obligatorio'
                valuePropName='checked'
              >
                <Switch />
              </Form.Item>
            </Col>
          </Row>
          <Row>
            <Col span={24}>
              <Form.Item
                label="Nueva Sección"
                name="seccion"
              >
                <Select
                  options={sectionListOptions}
                  placeholder="Seleccione una sección..."
                  onChange={(value) => {
                    setOrderChangeSection(sectionList.find(s => s.id === value).cantidad_preguntas);
                  }}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={40}>
            <Col span={24}>
              <Form.Item
                hidden={true}
                name="order"
              >
                <Input />
              </Form.Item>
            </Col>
          </Row>
          <Row justify={'center'}>
            <Col span={5}>
              <Form.Item>
                <CustomButton
                  type="primary"
                  htmlType="submit"
                  text='Mover Pregunta'
                  loading={savingQuestion}
                />
              </Form.Item>
            </Col>
            <Col span={5} style={{ marginLeft: '45px' }}>
              <Form.Item>
                <CustomButton
                  type="default"
                  htmlType="button"
                  text="Cerrar"
                  onClick={resetFormChangeQuestionSection}
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </>
  );
}
